import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { inr } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/lib/auth";
import { Search, RefreshCw, AlertTriangle, Info } from "lucide-react";
import { SavedFiguresTag, SAVED_PAIR_NOTE } from "@/components/SavedFiguresTag";

const SECTION_H2 = "-mx-4 -mt-4 mb-3 px-4 py-2 bg-navy text-white text-[11px] uppercase tracking-widest font-bold rounded-t-sm";
const H2_SUB = "text-white/75 font-semibold normal-case tracking-normal";

/**
 * AccountInfo — v37 round 2 PM rev 8.5 (2026-10-03 night).
 *
 * Transaction-level settlement mapping (user review 2026-10-03 night):
 *   • Cutoff anchor for "activity since last paid closing" is the
 *     period WINDOW END (Fri 23:59:59 IST) from `account_meta`,
 *     NOT `paid_at`. Correctly excludes same-day settled incomes.
 *   • Already-credited (unclosed) pair income is SEPARATE from
 *     newly-matched (projection) pair income.
 *   • Projected monthly bonus is zero when the achieved tier is for a
 *     prior month or already paid in the last closing. Admin sees
 *     evidence note.
 *   • "Wallet drift" is reframed as "Reconciliation variance (requires
 *     opening-balance evidence)" and is admin-only. IBAs do NOT see it.
 *   • Internal diagnostic terminology (source tags, ref_type codes,
 *     "canonical/legacy duplicates", scalar/ledger split) is hidden
 *     from the IBA view; full diagnostics remain for admin/superadmin.
 *
 * Pure read. Writes nothing.
 */
export default function AccountInfo() {
  const { user } = useAuth();
  const canSwitch = ["admin", "superadmin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTarget = (new URLSearchParams(window.location.search).get("user_code") || "").trim().toUpperCase();
  const [targetCode, setTargetCode] = useState(initialTarget);
  const [input, setInput] = useState(initialTarget);

  const fetchData = useCallback(() => {
    setLoading(true);
    setErr(null);
    const q = new URLSearchParams();
    if (targetCode) q.set("user_code", targetCode);
    q.set("page", "1");
    q.set("limit", "200");
    api
      .get(`/dist/account-info?${q.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [targetCode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applySwitch = () => {
    const code = input.trim().toUpperCase();
    setTargetCode(code);
    const url = new URL(window.location.href);
    if (code) url.searchParams.set("user_code", code);
    else url.searchParams.delete("user_code");
    window.history.replaceState({}, "", url.toString());
  };

  const asOfIst = useMemo(() => {
    if (!data?.generated_at_utc) return "—";
    try {
      const d = new Date(data.generated_at_utc);
      return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) + " IST";
    } catch { return data.generated_at_utc; }
  }, [data]);

  // Role gate — the backend already strips admin-only fields when
  // caller is distributor, but we also gate the UI chrome.
  const isAdminView = Boolean(data?.caller?.is_admin_view);
  const pb = data?.pair_and_bonus || {};
  const w = data?.wallet || {};

  // Reconciliation variance (admin-only — pre-v37 historical drift).
  const variance = Number(w.reconciliation_variance_rs || 0);
  const hasVariance = isAdminView && data && Math.abs(variance) >= 0.01;

  // v37 round 2 PM rev 8.5c (2026-10-03 night): normalize period_label
  // for grouping so retry/rerun/vN variants merge into the canonical
  // closing group. Hide groups whose only rows are internal
  // apply_paid_closing_decrement bookkeeping (gross=0, no payable).
  const groupedClosings = useMemo(() => {
    if (!data?.closings) return [];
    const canonicalLabel = (lbl) =>
      String(lbl || "(unlabelled)")
        .replace(/_retry[a-z0-9_]*$/i, "")
        .replace(/_v\d+$/i, "")
        .replace(/_rerun[a-z0-9_]*$/i, "");
    const groups = new Map();
    data.closings.forEach((c) => {
      const key = canonicalLabel(c.period_label);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    });
    const out = Array.from(groups.entries()).map(([period, rows]) => {
      const canonical = rows.find((r) => (r.gross_rs || 0) > 0 && (r.source || "") !== "apply_paid_closing_decrement") || rows[0];
      return { period, rows, canonical };
    });
    // Hide any group where the ONLY visible row is a zero-gross
    // bookkeeping entry (prevents stray "retry_after_deploy" cards).
    return out.filter((g) => {
      const canon = g.canonical;
      if (!canon) return false;
      const isBookkeepingOnly =
        (canon.source || "") === "apply_paid_closing_decrement" &&
        Number(canon.gross_rs || 0) <= 0;
      return !isBookkeepingOnly;
    });
  }, [data]);

  // Unpaid-committed discrepancy — admin only, framed as action-required.
  const unpaidCommitted = useMemo(() => {
    if (!isAdminView) return [];
    return groupedClosings
      .map((g) => g.canonical)
      .filter((r) => r && (r.gross_rs || 0) > 0 && (r.status || "pending_bank_transfer") !== "paid");
  }, [groupedClosings, isAdminView]);

  if (loading) {
    return (
      <DashboardLayout title="Account Information" subtitle="Loading…">
        <div className="p-6" data-testid="account-info-loading">Loading account snapshot…</div>
      </DashboardLayout>
    );
  }
  if (err) {
    return (
      <DashboardLayout title="Account Information" subtitle="Error">
        <div className="p-6 text-red-700" data-testid="account-info-error">Error: {err}</div>
      </DashboardLayout>
    );
  }
  if (!data) return null;

  return (
    <DashboardLayout
      title="Account Information"
      subtitle={`${data.user_code} · ${data.name}`}
    >
      <div className="space-y-5 font-medium text-navy" data-testid="account-info-page">
        {/* ========= As-of header + member switcher ========= */}
        <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-start gap-4 justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-navy/80 font-bold">Data as of</div>
            <div className="text-sm text-navy font-mono" data-testid="account-info-as-of">{asOfIst}</div>
            {isAdminView ? (
              <div className="text-[11px] text-navy/80 mt-0.5">
                Pure read · writes nothing · member: <span className="font-mono font-bold text-navy">{data.user_code}</span> · role: {data.role}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canSwitch ? (
              <div className="flex items-center gap-1.5 text-xs" data-testid="account-info-switcher">
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy/70" />
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") applySwitch(); }}
                    placeholder="SM747533"
                    data-testid="account-info-member-input"
                    className="border border-border rounded-sm pl-7 pr-3 py-1.5 text-xs font-mono uppercase focus:border-gold outline-none w-36"
                  />
                </div>
                <button
                  type="button"
                  onClick={applySwitch}
                  data-testid="account-info-member-apply"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-widest bg-navy text-white hover:bg-navy/90 rounded-sm"
                >
                  Load Member
                </button>
                {targetCode ? (
                  <button
                    type="button"
                    onClick={() => { setInput(""); setTargetCode(""); const url = new URL(window.location.href); url.searchParams.delete("user_code"); window.history.replaceState({}, "", url.toString()); }}
                    data-testid="account-info-member-self"
                    className="px-3 py-1.5 text-[10px] uppercase tracking-widest border border-border text-navy hover:bg-slate-50 rounded-sm"
                  >
                    My Record
                  </button>
                ) : null}
              </div>
            ) : null}
            <button
              type="button"
              onClick={fetchData}
              data-testid="account-info-refresh"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest border border-border text-navy hover:bg-slate-50 rounded-sm"
              title="Refresh snapshot"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {/* ========= Reconciliation variance (ADMIN ONLY) ========= */}
        {(hasVariance || unpaidCommitted.length > 0) ? (
          <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 space-y-2" data-testid="account-info-discrepancies">
            <div className="flex items-center gap-2 text-amber-900 font-bold">
              <AlertTriangle size={14} /> Admin diagnostics — reconciliation items
            </div>
            {hasVariance ? (
              <div className="text-xs text-amber-900" data-testid="disc-reconciliation-variance">
                <strong>Reconciliation variance:</strong> {inr(variance)} between <code>users.wallets.income</code> ({inr(w.scalar_users_wallets_income)}) and <code>wallet_txns</code> ledger ({inr(w.ledger_derived_balance)}). This is a VARIANCE pending opening-balance evidence from operator audit — NOT loss, debt, or missed payout. Do not overwrite balances to force agreement.
              </div>
            ) : null}
            {unpaidCommitted.length > 0 ? (
              <div className="text-xs text-amber-900" data-testid="disc-unpaid-committed">
                <strong>Committed but not marked paid in DB ({unpaidCommitted.length}):</strong>{" "}
                {unpaidCommitted.map((r) => `${(r.period_label || "").replace(/_/g, " ")} (${inr(r.net_rs)})`).join(", ")}.
                Flip status via the paid-status update workflow (NOT edited from this page).
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ========= Upcoming closing ========= */}
        <UpcomingClosingCard data={data} isAdminView={isAdminView} />

        {/* ========= Pair SP carry-forward (next closing) ========= */}
        <section data-testid="section-pair" className="bg-white border border-border rounded-sm p-4">
          <h2 className={SECTION_H2}>
            Pair SP Carry-Forward
            <span className={`ml-2 ${H2_SUB}`}>(remaining after this closing · continues to next week)</span>
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-900">Left SP (carry)</div>
              <div className="text-2xl text-emerald-800 tabular-nums font-bold" data-testid="pair-left-sp">{pb.pair_left_sp ?? "—"}</div>
              <div className="text-[10px] text-emerald-800 mt-0.5">available for next match on left leg</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-900">Right SP (carry)</div>
              <div className="text-2xl text-emerald-800 tabular-nums font-bold" data-testid="pair-right-sp">{pb.pair_right_sp ?? "—"}</div>
              <div className="text-[10px] text-emerald-800 mt-0.5">available for next match on right leg</div>
            </div>
          </div>
          <p className="text-[11px] text-navy/80 italic mt-2 leading-relaxed">
            Pair matching pays <strong>₹20/SP in 50-SP blocks</strong> (customer rule 19 Feb 2026). Sub-50 remainders stay here and carry to the next closing until the smaller leg reaches the next 50-SP milestone.
            {Number(pb.pair_left_sp || 0) < 50 && Number(pb.pair_right_sp || 0) < 50 ? (
              <span className="block mt-1 text-emerald-800">✓ Both legs under 50 SP — no further pair income until a leg grows to ≥50 SP.</span>
            ) : Math.min(Number(pb.pair_left_sp || 0), Number(pb.pair_right_sp || 0)) < 50 ? (
              <span className="block mt-1 text-amber-800">⚠ Smaller leg is under 50 SP — ₹0 next match until it grows to ≥50 SP.</span>
            ) : null}
          </p>
        </section>

        {/* ========= Bonus SP (1:2) carry-forward — separate from pair, never resets monthly ========= */}
        <BonusCarrySection pb={pb} />

        {/* ========= Wallet balance ========= */}
        <section data-testid="section-wallet" className="bg-white border border-border rounded-sm p-4">
          <h2 className={SECTION_H2}>Wallet Balance</h2>
          {isAdminView ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <WalletCell title="Pending next payout (net)" value={w.pending_next_payout_net_rs} testid="wallet-pending" />
                <WalletCell title="Scalar (users.wallets.income)" value={w.scalar_users_wallets_income} testid="wallet-scalar" />
                <WalletCell title="Effective available (post-paid)" value={w.effective_available_rs} testid="wallet-effective" />
                <WalletCell title="Settlement correction applied" value={w.settlement_correction_rs} testid="wallet-correction" />
                <WalletCell title="Ledger-derived balance" value={w.ledger_derived_balance} testid="wallet-ledger" />
                <WalletCell title="Ledger credits" value={w.ledger_derived_credit_sum} count={w.credit_rows_count} testid="wallet-credits" />
                <WalletCell title="Ledger debits" value={w.ledger_derived_debit_sum} count={w.debit_rows_count} testid="wallet-debits" />
                <WalletCell title="Settlements paid (DB · ledger)" value={w.paid_history_total_rs} count={w.paid_history_rows} testid="wallet-settlements-paid" />
              </div>
              {Number(w.settlement_correction_rs || 0) > 0 ? (
                <div className="text-[11px] text-emerald-800 italic mt-2 flex items-center gap-1">
                  <Info size={11} /> Display-only correction: last paid closing net ({inr(w.settlement_correction_rs)}) deducted from scalar for IBA-visible balance. No DB write.
                </div>
              ) : null}
              {hasVariance ? (
                <div className="text-[11px] text-amber-800 italic mt-2 flex items-center gap-1">
                  <AlertTriangle size={11} /> Reconciliation variance: {inr(Math.abs(variance))}. See admin diagnostics above.
                </div>
              ) : null}
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className={`${Number(w.pending_next_payout_net_rs || 0) > 0 ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"} border rounded-sm p-3`}>
                <div className="text-[10px] uppercase tracking-widest text-navy/80">Pending next payout{w.payment_hold ? <span className="ml-1 text-red-700 font-bold" data-testid="wallet-hold">· on hold</span> : null}</div>
                <div className={`text-2xl ${Number(w.pending_next_payout_net_rs || 0) > 0 ? "text-emerald-800" : "text-navy/70"} tabular-nums font-bold`} data-testid="wallet-available">
                  {inr(w.pending_next_payout_net_rs)}
                </div>
                {w.payment_hold ? (
                  <div className="text-[11px] text-red-800 font-semibold mt-1">Payment on hold — top up to 50 Self SP (Self SP {spFmt(w.self_sp)} · needs {spFmt(w.self_sp_needed)} more)</div>
                ) : null}
                {Number(w.pending_next_payout_net_rs || 0) > 0 ? (
                  <div className="text-[10px] text-navy/90 mt-1 font-mono">
                    gross {inr(w.pending_next_payout_gross_rs)} − TDS 2% {inr(w.pending_next_payout_tds_rs)} = net {inr(w.pending_next_payout_net_rs)}
                  </div>
                ) : (
                  <div className="text-[10px] text-navy/70 mt-0.5">
                    {data.account_meta?.last_cutoff_status === "finalized"
                      ? "no new earnings since the finalized closing"
                      : "no new earnings since last paid closing · you are paid up"}
                  </div>
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy/80">Settlements paid (lifetime)</div>
                <div className="text-2xl text-emerald-700 tabular-nums font-bold" data-testid="wallet-settled">{groupedClosings.length === 0 ? "No closing recorded" : inr(w.paid_history_total_rs)}</div>
                <div className="text-[10px] text-navy/70 mt-0.5">
                  {w.paid_history_rows || 0} closing{(w.paid_history_rows || 0) === 1 ? "" : "s"} · from closings ledger
                </div>
              </div>
            </div>
          )}
          {!isAdminView ? (
            <p className="text-[10px] text-navy/80 italic mt-2">
              "Pending next payout" will be transferred to your bank at the next operator-approved closing. Once paid, this value resets to ₹0 and the amount moves into "Settlements paid".
            </p>
          ) : null}
        </section>

        {/* ========= Dated SP movements ========= */}
        <section data-testid="section-sp-daily" className="bg-white border border-border rounded-sm p-4">
          <h2 className={SECTION_H2}>
            SP Movements (daily, IST) <span className={H2_SUB}>· {data.sp_movements_total || 0} source rows</span>
          </h2>
          {!data.sp_movements_daily?.length ? (
            <p className="text-xs text-navy/80 italic" data-testid="sp-empty">No SP adjustments on record.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Date (IST)</th>
                    <th className="text-right px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Δ Left</th>
                    <th className="text-right px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Δ Right</th>
                    {isAdminView ? <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Sources</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {data.sp_movements_daily.map((d) => (
                    <tr key={d.date_ist} className="border-b border-slate-100" data-testid={`sp-day-${d.date_ist}`}>
                      <td className="px-3 py-1 font-mono text-navy tabular-nums whitespace-nowrap">{d.date_ist}</td>
                      <td className="px-3 py-1 text-right tabular-nums text-emerald-700">{d.left_delta >= 0 ? `+${d.left_delta}` : d.left_delta}</td>
                      <td className="px-3 py-1 text-right tabular-nums text-emerald-700">{d.right_delta >= 0 ? `+${d.right_delta}` : d.right_delta}</td>
                      {isAdminView ? (
                        <td className="px-3 py-1 text-navy/90 text-[11px]">
                          {(d.sources || []).slice(0, 3).map((s, i) => (
                            <span key={i} className="inline-block mr-2">{s.reason || s.kind} <span className="text-navy/70">({s.actor || "—"})</span></span>
                          ))}
                          {(d.sources || []).length > 3 ? <span className="text-navy/70">… +{d.sources.length - 3}</span> : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.sp_movements_has_more ? <div className="text-[11px] text-navy/80 italic mt-2">(more pages available{isAdminView ? " — contact admin to export full history" : ""})</div> : null}
        </section>

        {/* ========= Daily income with components ========= */}
        <section data-testid="section-income-daily" className="bg-white border border-border rounded-sm p-4">
          <h2 className={SECTION_H2}>
            Daily Income (IST) · components by type <span className={H2_SUB}>· {data.income_total || 0} entries</span>
          </h2>
          {!data.income_daily?.length ? (
            <p className="text-xs text-navy/80 italic" data-testid="income-empty">No income on record.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Date (IST)</th>
                    <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Components</th>
                    <th className="text-right px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Total ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {data.income_daily.map((d) => (
                    <tr key={d.date_ist} className="border-b border-slate-100" data-testid={`inc-day-${d.date_ist}`}>
                      <td className="px-3 py-1 font-mono text-navy tabular-nums whitespace-nowrap">{d.date_ist}</td>
                      <td className="px-3 py-1 text-navy text-[11px]">
                        {Object.entries(d.by_type || {}).map(([t, amt]) => (
                          <span key={t} className="inline-block mr-3 whitespace-nowrap">
                            <span className="text-navy/80 capitalize">{String(t).replace(/_/g, " ")}:</span> <span className="font-mono font-semibold text-navy">{inr(amt)}</span>
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-1 text-right tabular-nums font-semibold text-navy">{inr(d.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.income_has_more ? <div className="text-[11px] text-navy/80 italic mt-2">(more pages available)</div> : null}
        </section>

        {/* ========= Payments & wallet debits =========
            v37 rev 8.5m (2026-10-03 night) — operator correction (Javed):
            removed for IBAs. "Closings History" below surfaces the same
            disbursement information (paid pill + period + net). Kept visible
            only in admin diagnostics view. */}
        {isAdminView ? (
          <section data-testid="section-payments" className="bg-white border border-border rounded-sm p-4">
          <h2 className={SECTION_H2}>
            Payments & Wallet Debits <span className={H2_SUB}>· {data.payments?.payouts_total || 0} payouts · {data.payments?.wallet_debits_total || 0} debits</span>
          </h2>
          {(!data.payments?.payouts?.length && !data.payments?.wallet_debits?.length) ? (
            <p className="text-xs text-navy/80 italic" data-testid="pay-empty">No disbursements or debits on record.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">When</th>
                    <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Kind</th>
                    <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Description</th>
                    <th className="text-right px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Amount</th>
                    {isAdminView ? <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Status / Ref</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {(data.payments.payouts || []).map((p) => (
                    <tr key={p.id || `${p.created_at}-${p.amount}`} className="border-b border-slate-100">
                      <td className="px-3 py-1 font-mono text-navy tabular-nums text-[11px] whitespace-nowrap">{String(p.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-1 text-navy"><span className="text-emerald-700 font-bold">Settlement</span></td>
                      <td className="px-3 py-1 text-navy">{p.description || p.note || "—"}</td>
                      <td className="px-3 py-1 text-right tabular-nums font-semibold text-emerald-700">{inr(p.amount)}</td>
                      {isAdminView ? <td className="px-3 py-1 text-navy">{p.status || "—"}</td> : null}
                    </tr>
                  ))}
                  {(data.payments.wallet_debits || []).map((d) => (
                    <tr key={d.id || `${d.created_at}-${d.amount}`} className="border-b border-slate-100">
                      <td className="px-3 py-1 font-mono text-navy tabular-nums text-[11px] whitespace-nowrap">{String(d.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-1 text-navy"><span className="text-red-700 font-bold">Debit</span></td>
                      <td className="px-3 py-1 text-navy">{d.description || (isAdminView ? d.ref_type : "Settlement debit") || "—"}</td>
                      <td className="px-3 py-1 text-right tabular-nums font-semibold text-red-700">− {inr(d.amount)}</td>
                      {isAdminView ? <td className="px-3 py-1 text-navy font-mono text-[11px]">{d.ref_type || "—"}</td> : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        ) : null}

        {/* ========= Carry-forward for every week ========= */}
        <CarryForwardByWeek cw={data.carry_weeks} />

        {/* ========= Closings history (grouped, with payment pill) ========= */}
        <section data-testid="section-closings" className="bg-white border border-border rounded-sm p-4">
          <h2 className={SECTION_H2}>
            Closings History <span className={H2_SUB}>· {groupedClosings.length} periods{isAdminView ? ` · ${data.closings_total || 0} ledger rows` : ""}</span>
          </h2>
          {!groupedClosings.length ? (
            <p className="text-xs text-navy/80 italic" data-testid="closings-empty">No closing recorded</p>
          ) : (
            <div className="space-y-3">
              {groupedClosings.map((g) => {
                const canon = g.canonical;
                const status = canon.status || "pending_bank_transfer";
                const gross = Number(canon.gross_rs || 0);
                const label = String(g.period).replace(/_/g, " ").replace(/\bto\b/, "→");
                return (
                  <div key={g.period} className="border border-border rounded-sm p-3 bg-slate-50/40" data-testid={`closing-group-${g.period}`}>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                      <span className="font-serif text-base text-navy">{label}</span>
                      {canon.committed_at_ist ? <span className="text-xs text-navy/90">· closed {canon.committed_at_ist}</span> : null}
                      <ClosingStatusPill status={status} gross_rs={gross} />
                      {isAdminView && g.rows.length > 1 ? (
                        <span className="text-[10px] text-navy/80 italic">· {g.rows.length} ledger rows (incl. legacy duplicates)</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-navy">
                      <strong>Gross:</strong> {inr(canon.gross_rs)} ·
                      <strong> TDS:</strong> {inr(canon.tds_amount_rs)} ·
                      <strong> Net:</strong> <span className="text-emerald-700 font-bold">{inr(canon.net_rs)}</span>
                      <span className="mx-2 text-navy/60">·</span>
                      <strong>Pair:</strong> {inr(canon.pair_income_rs)} ·
                      <strong> Direct Ref:</strong> {inr(canon.direct_ref_rs)} ·
                      <strong> Monthly Bonus:</strong> {inr(canon.monthly_bonus_rs)}
                    </div>
                    <div className="text-xs text-emerald-800 mt-0.5" data-testid={`closing-carry-${g.period}`}>
                      <strong>Carry-forward after this closing —</strong> Pair L/R: {canon.pair_carry_left_sp ?? "—"} / {canon.pair_carry_right_sp ?? "—"}
                      <span className="mx-2 text-navy/60">·</span>
                      Bonus L/R: {canon.bonus_carry_left_sp ?? "—"} / {canon.bonus_carry_right_sp ?? "—"}
                    </div>
                    {isAdminView ? (
                      <div className="text-[11px] text-navy/80 mt-0.5">
                        Pair legs: {canon.left_sp_before ?? "—"} → {canon.left_sp_after ?? "—"} L · {canon.right_sp_before ?? "—"} → {canon.right_sp_after ?? "—"} R
                        {canon.source ? <span className="ml-2 font-mono text-[10px]">· source: {canon.source}</span> : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {isAdminView ? (
          <p className="text-[10px] text-navy/80 italic flex items-center gap-1">
            <Info size={10} /> Admin view · pure read · no settlement, backfill, or wallet writes from this page.
          </p>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

function PaymentHoldNote({ upc }) {
  const startup = Number(upc.startup_wallet_rs || 0);
  return (
    <div className="mb-3 border border-red-300 bg-red-50 rounded-sm p-3 text-xs text-red-900" data-testid="payment-hold-note">
      <div className="font-bold">Payment on hold — top up to 50 Self SP</div>
      <div className="mt-0.5">
        Self SP <strong data-testid="payment-hold-self-sp">{spFmt(upc.self_sp)}</strong> · needs <strong data-testid="payment-hold-needed">{spFmt(upc.self_sp_needed)}</strong> more Self SP.
        All earned income{startup > 0 ? ` (including startup ${inr(startup)} in wallet)` : ""} and pair / bonus carry-forward below are kept and released at the first weekly closing after you reach 50 Self SP.
      </div>
    </div>
  );
}

function UpcomingClosingCard({ data, isAdminView }) {
  // v37 round 2 PM rev 8.5: Backend-computed cutoff + split tiles.
  // "Already Credited (unclosed)" vs "Newly Matched (projection)" — mutually exclusive.
  const meta = data?.account_meta || {};
  const upc = meta.upcoming || {};
  const incomeDaily = data?.income_daily || [];
  const spMovements = data?.sp_movements_daily || [];
  const pb = data?.pair_and_bonus || {};

  const cutoffIso = meta.last_paid_window_end_ist || meta.last_paid_at;
  const cutoffDt = cutoffIso ? new Date(cutoffIso) : null;
  const now = new Date();
  const elapsedDays = cutoffDt ? Math.max(0, Math.floor((now - cutoffDt) / 86400000)) : null;
  const lastPaidIst = cutoffDt
    ? cutoffDt.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" })
    : null;

  const fmtPeriod = (s) => String(s || "").replace(/_/g, " ").replace(/\bto\b/, "→");
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const inrFmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  // Filter income_daily to STRICTLY after period_end_ist cutoff.
  const sinceCutoff = (dateIst) => {
    if (!cutoffDt) return true;
    try {
      // Compare end-of-IST-day to cutoff.
      const d = new Date(dateIst + "T23:59:59+05:30");
      return d > cutoffDt;
    } catch { return false; }
  };
  const recentIncome = incomeDaily.filter((row) => sinceCutoff(row.date_ist));
  const recentSp = spMovements.filter((row) => sinceCutoff(row.date_ist));

  const alreadyPair  = Number(upc.already_credited_unclosed_pair_rs || 0);
  const alreadyDref  = Number(upc.already_credited_unclosed_dref_rs || 0);
  const alreadyBonus = Number(upc.already_credited_unclosed_bonus_rs || 0);
  const alreadyOther = Number(upc.already_credited_unclosed_other_rs || 0);
  const alreadyTotal = Number(upc.already_credited_unclosed_total_rs || 0);

  const newMatchedSp = Number(upc.projected_newly_matched_pair_sp || 0);
  const newMatchedRs = Number(upc.projected_newly_matched_pair_rs || 0);
  const projBonus   = Number(upc.projected_bonus_rs || 0);
  const projBonusTier = Number(upc.projected_bonus_tier || 0);
  const projBonusNote = String(upc.projected_bonus_note || "");
  const lastPaidBonusRs = Number(upc.last_paid_bonus_rs || 0);

  const projGross = Number(upc.projected_gross_rs || 0);
  const projTds   = Number(upc.projected_tds_rs || 0);
  const projNet   = Number(upc.projected_net_rs || 0);
  const earnedGross = Number(upc.earned_gross_rs ?? projGross);
  const withdrawn = Number(upc.withdrawn_deduction_rs || 0);

  const hasAnyActivity = alreadyTotal > 0 || newMatchedRs > 0 || projBonus > 0 || recentSp.length > 0;

  // Friendly projection note for IBAs / admin.
  const bonusNoteLabel = (() => {
    if (projBonusNote.startsWith("level_")) return `Level ${projBonusTier} reached · paid at next closing`;
    if (projBonusNote === "next_level_not_reached") return `Next level not reached yet (carry ${pb.monthly_bonus_carry_left_sp ?? 0}/${pb.monthly_bonus_carry_right_sp ?? 0} L/R)`;
    if (projBonusNote === "all_levels_paid") return "All levels already paid";
    if (projBonusNote === "inactive") return "Inactive — no income";
    return projBonusNote || (lastPaidBonusRs ? `Last paid ${inrFmt(lastPaidBonusRs)}` : "");
  })();

  return (
    <section data-testid="section-upcoming-closing" className="bg-gradient-to-br from-navy/5 via-white to-emerald-50/30 border-2 border-dashed border-navy/30 rounded-sm p-4">
      <div className="-mx-4 -mt-4 mb-3 px-4 py-2 bg-navy rounded-t-sm flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="text-[11px] uppercase tracking-widest text-white font-bold">Upcoming Closing · Pending Commit</h2>
        {upc.payment_hold ? (
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-red-100 text-red-800" data-testid="upcoming-status">
            Payment on hold — top up to 50 Self SP
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800" data-testid="upcoming-status">
            ⏳ Will commit at next operator-approved closing
          </span>
        )}
      </div>
      {upc.payment_hold ? <PaymentHoldNote upc={upc} /> : null}
      {cutoffDt ? (
        <div className="text-xs text-navy mb-3" data-testid="upcoming-range">
          Accumulation window: <strong>{lastPaidIst}</strong>
          <span className="text-navy/70"> (end of last {meta.last_cutoff_status === "finalized" ? "finalized" : "paid"} closing {fmtPeriod(meta.last_paid_period_label)})</span>
          <span className="mx-1 text-navy/70">→</span>
          <strong>today, {fmtDate(now.toISOString())}</strong>
          {elapsedDays !== null ? <span className="text-navy/70"> · {elapsedDays} day{elapsedDays === 1 ? "" : "s"} elapsed</span> : null}
        </div>
      ) : (
        <div className="text-xs text-navy mb-3 italic">No previous paid closing — this is the first upcoming closing on record.</div>
      )}

      {/* ───── Already-credited (unclosed) tiles ───── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Pair income (credited)</div>
          <div className="text-xl text-emerald-800 tabular-nums font-bold" data-testid="upcoming-already-pair">{inrFmt(alreadyPair)}</div>
          <div className="text-[10px] text-navy/80 mt-0.5">from binary matching · in wallet</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Direct Ref (credited)</div>
          <div className="text-xl text-emerald-800 tabular-nums font-bold" data-testid="upcoming-already-dref">{inrFmt(alreadyDref)}</div>
          <div className="text-[10px] text-navy/80 mt-0.5">daily referral commissions</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Monthly Bonus (credited)</div>
          <div className="text-xl text-emerald-800 tabular-nums font-bold" data-testid="upcoming-already-bonus">{inrFmt(alreadyBonus)}</div>
          <div className="text-[10px] text-navy/80 mt-0.5">tier bonus · in wallet</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Other (credited)</div>
          <div className="text-xl text-emerald-800 tabular-nums font-bold" data-testid="upcoming-already-other">{inrFmt(alreadyOther)}</div>
          <div className="text-[10px] text-navy/80 mt-0.5">booster · startup · retail</div>
        </div>
      </div>

      {/* ───── Newly matched (projection) tiles ───── */}
      <div className="mb-2 text-[10px] uppercase tracking-widest text-navy/80 font-bold">Projection from current SP legs (not yet credited)</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-sky-50 border border-sky-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Newly Matched Pair ₹</div>
          <div className="text-xl text-sky-800 tabular-nums font-bold" data-testid="upcoming-newly-matched">{inrFmt(newMatchedRs)}</div>
          <div className="text-[10px] text-navy/80 mt-0.5">{newMatchedSp} SP matched · {pb.pair_left_sp ?? 0}/{pb.pair_right_sp ?? 0} L/R</div>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Bonus (1:2) due ₹</div>
          <div className="text-xl text-sky-800 tabular-nums font-bold" data-testid="upcoming-proj-bonus">{inrFmt(projBonus)}</div>
          <div className="text-[10px] text-navy/80 mt-0.5">{bonusNoteLabel}</div>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 col-span-2 md:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-navy/80">Reconciliation (upcoming)</div>
          <div className="text-sm text-navy font-mono mt-1" data-testid="upcoming-reconcile">
            {withdrawn > 0 ? (
              <>
                <span data-testid="upcoming-withdrawn">Earned <strong>{inrFmt(earnedGross)}</strong> − Already withdrawn <strong>{inrFmt(withdrawn)}</strong></span>
                <span className="text-navy/70"> = </span>
              </>
            ) : null}
            Gross <strong>{inrFmt(projGross)}</strong>
            <span className="text-navy/70"> − </span>
            TDS 2% <strong>{inrFmt(projTds)}</strong>
            <span className="text-navy/70"> = </span>
            <span className="text-emerald-700 font-bold">Net {inrFmt(projNet)}</span>
          </div>
          <div className="text-[10px] text-navy/80 mt-0.5">
            {withdrawn > 0 ? "Earned" : "Gross"} = already-credited ({inrFmt(alreadyTotal)}) + newly-matched ({inrFmt(newMatchedRs)}) + projected-bonus ({inrFmt(projBonus)})
          </div>
        </div>
      </div>

      {!hasAnyActivity ? (
        <div className="text-[11px] text-navy/80 italic mb-2" data-testid="upcoming-no-activity">No new income credited since last paid closing. Pair + monthly bonus for the upcoming closing will be computed at commit time.</div>
      ) : null}

      {/* Daily activity table since cutoff. */}
      {recentIncome.length > 0 ? (
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-widest text-navy/80 font-bold mb-1">Daily activity (strictly after period end)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-1 text-navy/90 uppercase tracking-wider font-medium">Date (IST)</th>
                  <th className="text-left px-3 py-1 text-navy/90 uppercase tracking-wider font-medium">Components credited</th>
                  <th className="text-right px-3 py-1 text-navy/90 uppercase tracking-wider font-medium">Total ₹</th>
                </tr>
              </thead>
              <tbody>
                {recentIncome.map((d) => (
                  <tr key={d.date_ist} className="border-b border-slate-100" data-testid={`upcoming-day-${d.date_ist}`}>
                    <td className="px-3 py-1 font-mono text-navy tabular-nums whitespace-nowrap">{d.date_ist}</td>
                    <td className="px-3 py-1 text-navy text-[11px]">
                      {Object.entries(d.by_type || {}).map(([t, amt]) => (
                        <span key={t} className="inline-block mr-2 whitespace-nowrap">
                          <span className="text-navy/80 capitalize">{String(t).replace(/_/g, " ")}:</span> <span className="font-mono font-semibold text-navy">{inrFmt(amt)}</span>
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-1 text-right tabular-nums font-semibold text-navy">{inrFmt(d.total)}</td>
                  </tr>
                ))}
                <tr className="bg-emerald-50">
                  <td className="px-3 py-1 text-[10px] uppercase tracking-widest text-navy/90 font-bold">Total already credited</td>
                  <td className="px-3 py-1 text-[10px] text-navy/90">
                    {Object.entries(upc.already_credited_unclosed_by_type || {}).map(([t, amt]) => (
                      <span key={t} className="inline-block mr-2 whitespace-nowrap"><span className="capitalize">{String(t).replace(/_/g, " ")}:</span> <strong>{inrFmt(amt)}</strong></span>
                    ))}
                  </td>
                  <td className="px-3 py-1 text-right tabular-nums font-bold text-emerald-700" data-testid="upcoming-already-total">{inrFmt(alreadyTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* New SP movements since cutoff. */}
      {recentSp.length > 0 ? (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-widest text-navy/80 font-bold mb-1">New SP movements (strictly after period end)</div>
          <div className="text-[11px] text-navy space-y-0.5">
            {recentSp.map((d) => (
              <div key={d.date_ist} className="flex gap-3 whitespace-nowrap" data-testid={`upcoming-sp-${d.date_ist}`}>
                <span className="font-mono text-navy/90 tabular-nums">{d.date_ist}</span>
                <span className="text-emerald-700">L {d.left_delta >= 0 ? `+${d.left_delta}` : d.left_delta}</span>
                <span className="text-emerald-700">R {d.right_delta >= 0 ? `+${d.right_delta}` : d.right_delta}</span>
                {isAdminView ? (
                  <span className="text-navy/80 text-[10px]">
                    {(d.sources || []).slice(0, 2).map((s, i) => (
                      <span key={i}>{s.reason || s.kind}{i === 0 && (d.sources || []).length > 1 ? ", " : ""}</span>
                    ))}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <p className="text-[10px] text-navy/80 italic mt-3">
        <strong>How this is calculated:</strong> Already-credited components come from your incomes rows dated strictly AFTER the last paid closing's period end ({lastPaidIst ? `${lastPaidIst}, 23:59 IST` : "—"}). Newly-matched pair = smaller leg in 50-SP blocks × ₹20 from current standing legs. Bonus (1:2) is paid when the bonus SP carry-forward reaches the next level not yet paid; that level's SP is then used up and the rest carries forward. TDS 2% applies. Final payout is determined at operator-approved closing commit; this view is READ-ONLY and no DB value changes from this page.
      </p>
    </section>
  );
}

function BonusCarrySection({ pb }) {
  const nxt = pb.next_bonus_level;
  const due = pb.bonus_levels_due || [];
  return (
    <section data-testid="section-bonus" className="bg-white border border-border rounded-sm p-4">
      <h2 className={SECTION_H2}>
        Bonus SP (1:2) Carry-Forward <span className={H2_SUB}>· separate from pair SPs · continues every week and month</span>
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BonusCell title="Bonus SP received L" value={pb.monthly_left_sp_raw} testid="bonus-raw-left" />
        <BonusCell title="Bonus SP received R" value={pb.monthly_right_sp_raw} testid="bonus-raw-right" />
        <BonusCell title="Used by paid levels L" value={pb.monthly_bonus_consumed_left_sp} testid="bonus-cons-left" />
        <BonusCell title="Used by paid levels R" value={pb.monthly_bonus_consumed_right_sp} testid="bonus-cons-right" />
        <BonusCell title="Carry-forward L (bonus)" value={pb.monthly_bonus_carry_left_sp} testid="bonus-carry-left" accent />
        <BonusCell title="Carry-forward R (bonus)" value={pb.monthly_bonus_carry_right_sp} testid="bonus-carry-right" accent />
        <BonusCell title="After next closing L" value={pb.bonus_after_left_sp} testid="bonus-after-left" />
        <BonusCell title="After next closing R" value={pb.bonus_after_right_sp} testid="bonus-after-right" />
      </div>
      <div className="text-[11px] text-navy mt-2 space-y-0.5">
        <div data-testid="bonus-level-paid">Highest level paid (lifetime): <strong>{pb.achieved_tier ? `Level ${pb.achieved_tier}` : "none"}</strong> <span className="text-navy/70 italic">· each level is paid once</span></div>
        {due.length ? (
          <div className="text-emerald-800" data-testid="bonus-due">
            Due at next closing: <strong>{due.map((x) => `Level ${x.tier} (${x.used_left_sp}:${x.used_right_sp} SP)`).join(" + ")} = ₹{Number(pb.bonus_due_rs || 0).toLocaleString("en-IN")}</strong>
          </div>
        ) : nxt ? (
          <div data-testid="bonus-next-level">Next level: <strong>Level {nxt.tier}</strong> needs {nxt.high_sp} : {nxt.match_sp} SP (bigger : smaller side) · ₹{Number(nxt.bonus).toLocaleString("en-IN")}</div>
        ) : null}
      </div>
    </section>
  );
}

const spFmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const lr = (a) => (Array.isArray(a) && a[0] != null ? `${spFmt(a[0])} / ${spFmt(a[1])}` : "—");
const periodText = (p) => String(p || "—").replace(/_/g, " ").replace(/\bto\b/, "→");
const CF_COLS = ["opening", "new", "available", "consumed", "remaining"];

function CarryForwardByWeek({ cw }) {
  const weeks = [...(cw?.upcoming ? [cw.upcoming] : []), ...(cw?.weeks || [])];
  const th = "text-right px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium whitespace-nowrap";
  return (
    <section data-testid="section-carry-weeks" className="bg-white border border-border rounded-sm p-4">
      <h2 className={SECTION_H2}>
        Carry-Forward by Week <span className={H2_SUB}>· pair SP and bonus SP kept separately · Left / Right</span>
      </h2>
      <p className="text-[11px] text-navy/90 mb-3">Opening + New − Consumed = Remaining. Pair uses 1:1 in 50-SP blocks; bonus uses the next unpaid level. Pair never reduces bonus SP and bonus never reduces pair SP.</p>
      {weeks.length === 0 ? (
        <p className="text-xs text-navy/80 italic" data-testid="carry-weeks-empty">No closing recorded</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Week</th>
                <th className="text-left px-3 py-1.5 text-navy/90 uppercase tracking-wider font-medium">Balance</th>
                {CF_COLS.map((c) => <th key={c} className={th}>{c}</th>)}
                <th className={th}>Income</th>
              </tr>
            </thead>
            <tbody>{weeks.map((w) => <CarryWeekRows key={`${w.status}-${w.period_label}`} w={w} />)}</tbody>
          </table>
          {weeks.some((w) => w.status === "no_closing") ? (
            <p className="text-[10px] text-navy/80 mt-1" data-testid="carry-weeks-reconstructed-note">* Reconstructed balance — not a saved closing result.</p>
          ) : null}
          {weeks.some((w) => w.pair_source === "saved") ? (
            <p className="text-[10px] text-sky-900 mt-1" data-testid="carry-weeks-saved-note">{SAVED_PAIR_NOTE}</p>
          ) : null}
        </div>
      )}
    </section>
  );
}

function CarryWeekRows({ w }) {
  const now = w.status === "upcoming";
  const noRow = w.status === "no_closing";
  const id = now ? "now" : w.period_label;
  const cell = "px-3 py-1 text-right tabular-nums whitespace-nowrap";
  const bonusIncome = w.bonus_rs > 0 ? `${inr(w.bonus_rs)}${w.bonus_level ? ` · L${w.bonus_level}` : ""}` : inr(0);
  const lines = [["Pair", w.pair, noRow ? "—" : inr(w.pair_income_rs)], ["Bonus", w.bonus, noRow ? "—" : bonusIncome]];
  return (
    <>
      {lines.map(([name, b, income], i) => (
        <tr key={name} className={`${i ? "border-b border-slate-200" : ""} ${now ? "bg-emerald-50/60" : ""}`} data-testid={`carry-week-${id}-${name.toLowerCase()}`}>
          {i === 0 ? (
            <td rowSpan={2} className="px-3 py-1 align-top text-navy border-b border-slate-200 whitespace-nowrap">
              <div className={now ? "font-semibold text-navy" : ""}>{now ? "This week · next closing" : `After ${periodText(w.period_label)}`}</div>
              {now ? <div className="text-[10px] text-navy/80">{periodText(w.period_label)} · live</div> : null}
              {noRow ? (
                <div className="text-[10px] text-amber-800 font-bold" data-testid={`carry-week-${id}-no-closing`}>No closing recorded · reconstructed*</div>
              ) : null}
              {!now && !noRow && w.status !== "paid" ? (
                <div className="text-[10px] text-amber-800 font-bold" data-testid={`carry-week-${id}-finalized`}>Closing finalized · Payment pending</div>
              ) : null}
              {now && w.payment_hold ? (
                <div className="text-[10px] text-red-700 font-bold" data-testid="carry-week-now-hold">Payment on hold — top up to 50 Self SP</div>
              ) : null}
              {!w.pair.reconciles || !w.bonus.reconciles ? (
                <div className="text-[10px] text-amber-700" data-testid={`carry-week-${id}-legacy`}>legacy stored snapshot</div>
              ) : null}
            </td>
          ) : null}
          <td className="px-3 py-1 font-medium text-navy">
            {name}
            {i === 0 && w.pair_source === "saved" ? (
              <div><SavedFiguresTag kind={now ? "opening" : noRow ? "carried" : "row"} testid={`carry-week-${id}-pair-saved`} /></div>
            ) : null}
          </td>
          {CF_COLS.map((c) => (
            <td key={c} className={`${cell} ${c === "remaining" ? "font-bold text-emerald-800" : ""}`} data-testid={`carry-week-${id}-${name.toLowerCase()}-${c}`}>{lr(b?.[c])}{noRow && c === "remaining" ? "*" : ""}</td>
          ))}
          <td className={cell} data-testid={`carry-week-${id}-${name.toLowerCase()}-income`}>{income}</td>
        </tr>
      ))}
      {now && w.bonus_rs > 0 ? (
        <tr className="bg-emerald-50/60 border-b border-slate-200">
          <td colSpan={8} className="px-3 pb-2 text-[11px] text-emerald-900" data-testid="carry-week-now-bonus-note">
            Bonus remaining {lr(w.bonus.remaining)} applies once the {inr(w.bonus_rs)} bonus is processed at closing. Until then the bonus balance stays {lr(w.bonus.available)}.
          </td>
        </tr>
      ) : null}
    </>
  );
}

function BonusCell({ title, value, testid, sub, accent }) {
  return (
    <div className={`${accent ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"} border rounded-sm p-3`}>
      <div className="text-[10px] uppercase tracking-widest text-navy/80">{title}</div>
      <div className={`text-xl ${accent ? "text-emerald-800" : "text-navy"} tabular-nums font-bold`} data-testid={testid}>
        {value ?? "—"}
      </div>
      {sub ? <div className="text-[10px] text-navy/70 mt-0.5 font-mono">{sub}</div> : null}
    </div>
  );
}

function WalletCell({ title, value, count, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-widest text-navy/80">{title}</div>
      <div className="text-xl text-navy tabular-nums font-bold" data-testid={testid}>{inr(value)}</div>
      {typeof count === "number" ? <div className="text-[10px] text-navy/70 mt-0.5">{count} rows</div> : null}
    </div>
  );
}

function ClosingStatusPill({ status, gross_rs }) {
  const zero = Number(gross_rs || 0) === 0;
  if (status === "paid" && !zero) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800" data-testid="closing-pill">
        ✓ Paid
      </span>
    );
  }
  if (status === "paid" && zero) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600" data-testid="closing-pill">
        — Zero Payout
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800" data-testid="closing-pill">
      ⏱ Closing finalized · Payment pending
    </span>
  );
}
